import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SubmitForReviewSectionProps {
  reviewStatus: string | null | undefined;
  profileCompletionPercentage: number;
  displayName: string;
  isSubmitting: boolean;
  onSubmit: () => void;
}

export function SubmitForReviewSection({
  reviewStatus,
  profileCompletionPercentage,
  displayName,
  isSubmitting,
  onSubmit,
}: SubmitForReviewSectionProps) {
  if (!reviewStatus || (reviewStatus !== 'draft' && reviewStatus !== 'changes_requested')) {
    return null;
  }

  const isComplete = profileCompletionPercentage === 100;

  return (
    <div className={cn(
      "rounded-xl sm:rounded-[20px] p-4 sm:p-6 border",
      isComplete
        ? "bg-[#4669ed]/5 border-[#4669ed]/20"
        : "bg-amber-50/50 border-amber-200/50"
    )}>
      {isComplete ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h4 className="font-medium text-slate-900">
              {reviewStatus === 'changes_requested' ? 'Resubmit for Review' : 'Submit for Review'}
            </h4>
            <p className="text-sm text-slate-500">
              {reviewStatus === 'changes_requested'
                ? 'Make the requested changes and resubmit your profile'
                : 'Submit your profile for admin review to make it visible to clients'}
            </p>
          </div>
          <Button
            onClick={onSubmit}
            disabled={isSubmitting || !displayName.trim()}
            className="bg-[#4669ed] hover:bg-[#2b42a3] rounded-[48px]"
          >
            {isSubmitting ? 'Submitting...' : 'Submit for Review'}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-medium text-slate-900">
              Complete Your Profile to Submit
            </h4>
            <p className="text-sm text-slate-500">
              Your profile is {profileCompletionPercentage}% complete. Please complete all required sections (100%) before submitting for review.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
